import React, { useMemo } from "react";
import { _t, isEn } from "app/i18n/i18n";
import { Button, Modal } from "react-bootstrap";
import useSubscriptions from "lib/hooks/useSubscriptions/useSubscriptions";
import SubscriptionsPillsSection from "components/Subscriptions/SubscriptionsPillsSection/SubscriptionsPillsSection";
import SubscriptionsForecast from "components/Subscriptions/SubscriptionsForecast/SubscriptionsForecast";
import { useOnSubscribe } from "lib/hooks/useOnSubscribe/useOnSubscribe";
import { useQueryParams } from "lib/hooks/useQueryParams";
import { getUnsupportedFilterNames, buildUnsupportedFiltersMessage } from "lib/hooks/useSubscriptions/useSubscriptions.helpers";
import { useValidatedSubscriptionTags } from "./SubscriptionsModal.helpers";
import { useSubscriptionDeepLink } from "lib/hooks/useSubscriptions/useSubscriptionDeepLink";
import { LoadingSpinner } from "lib/Spinner";
import styles from "./SubscriptionsModal.module.scss";
import SubscriptionsBellButton from "../SubscriptionsBellButton/SubscriptionsBellButton";
import { useGetPreferences } from "../../../api/preferences/useGetPreferences";
import { PUB_LANG } from "../../../redux/slices/api/initialisePreferences";
import { SubscriptionLanguagePills } from "../SubscriptionsPillsSection/SubscriptionLanguagePills";
import { auditShareSubscriptionLink } from "../../../lib/analytics/auditEvents";
import { getGoHost } from "lib/routing";
import { features } from "app/features";

function SubscriptionsModalContent({
                                     selectedSubscriptionTags,
                                     setSelectedSubscriptionTags,
                                     isSubscriptionFromPillsBar,
                                     selectedLanguages,
                                     setSelectedLanguages,
                                     selectedPub,
                                     defaultCategoriesAndTags,
                                     reportLang,
                                     qs,
                                     onSubscribe,
                                     handleClose,
                                   }) {
  const {
    validatedTags,
    validatedCategoriesAndTags,
    isValidating,
    isValidationComplete,
  } = useValidatedSubscriptionTags(
    selectedSubscriptionTags,
    defaultCategoriesAndTags,
    selectedLanguages,
    selectedPub,
    isSubscriptionFromPillsBar,
  );

  const unsupportedFilterNames = useMemo(
    () => (isSubscriptionFromPillsBar ? getUnsupportedFilterNames(qs) : []),
    [isSubscriptionFromPillsBar, qs],
  );
  const unsupportedMessage = useMemo(
    () => buildUnsupportedFiltersMessage(unsupportedFilterNames),
    [unsupportedFilterNames],
  );

  const isLangLocked = Array.isArray(reportLang) && reportLang.length === 1;
  const lockedLang = isLangLocked ? reportLang[0] : null;

  const langValues = useMemo(() => {
    const en = {
      code: "en",
      label: _t("English"),
      selected: selectedLanguages.includes("en"),
      disabled: isLangLocked && lockedLang === "en",
    };
    const ja = {
      code: "ja",
      label: _t("Japanese"),
      selected: selectedLanguages.includes("ja"),
      disabled: isLangLocked && lockedLang === "ja",
    };

    if (isLangLocked && (lockedLang === "en" || lockedLang === "ja")) {
      return lockedLang === "en" ? [en] : [ja];
    }

    return [en, ja];
  }, [selectedLanguages, isLangLocked, lockedLang]);

  const noAvailableTags =
    !validatedCategoriesAndTags?.length ||
    validatedCategoriesAndTags.every(([, tags]) => !tags?.length);
  const hasTags =
    Array.isArray(validatedTags) && validatedTags.some((t) => t && t.selected === true);
  const hasLanguage =
    !isSubscriptionFromPillsBar ||
    (isLangLocked || (selectedLanguages && selectedLanguages.length > 0));

  const handleShareLink = () => {
    const baseUrl = `${window.location.protocol}//${getGoHost()}`;
    const link = isSubscriptionFromPillsBar
      ? (() => { const u = new URL(window.location.pathname + window.location.search, baseUrl); u.searchParams.set("subscribe", "true"); return u.toString(); })()
      : `${baseUrl}${window.location.pathname}?q=${selectedPub.id}&subscribe=true`;
    auditShareSubscriptionLink(selectedPub?.id, "subscription-modal");
    const subject = encodeURIComponent("Subscribe to Nomura Research");
    window.open(`mailto:?subject=${subject}&body=${encodeURIComponent(link)}`, "_self");
  };

  return (
    <>
      <Modal.Body className={styles.body}>
        {isValidating ? (
          <div className={styles.loadingContainer}>
            <LoadingSpinner className="mx-auto pt-5 pb-5" />
          </div>
        ) : (
          <>
            {unsupportedMessage && (
              <div className={styles.unsupportedFiltersWarning}>
                <div className={styles.warningIcon} aria-hidden="true">!</div>
                <span>{unsupportedMessage}</span>
              </div>
            )}
            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTitle}>{_t("Select Tags")}</div>
                {isSubscriptionFromPillsBar && (
                  <div className={styles.sectionTitleRight} aria-label="Select language">
                    <SubscriptionLanguagePills
                      values={langValues}
                      onToggle={(code) => {
                        if (isLangLocked && code === lockedLang) return;
                        setSelectedLanguages((prev) => {
                          const has = prev.includes(code);
                          return has ? prev.filter((c) => c !== code) : [...prev, code];
                        });
                      }}
                    />
                  </div>
                )}
              </div>

              <SubscriptionsPillsSection
                selectedSubscriptionTags={selectedSubscriptionTags}
                setSelectedSubscriptionTags={setSelectedSubscriptionTags}
                validatedCategoriesAndTags={validatedCategoriesAndTags}
              />
              {noAvailableTags && (
                <div className={styles.langError}>
                  {_t("There are no core relevant tags for this report.")}
                </div>
              )}
            </div>
            <div className={styles.sectionCard}>
              <div className={styles.sectionTitle}>{_t("Estimated Rate of Delivery")}</div>
              {hasTags && hasLanguage && isValidationComplete ? (
                <SubscriptionsForecast
                  selectedLanguages={selectedLanguages}
                  selectedSubscriptionTags={validatedTags}
                  isSubscriptionFromPillsBar={isSubscriptionFromPillsBar}
                  selectedPub={selectedPub}
                />
              ) : (
                <div className={styles.noTagsCard}>
                  <div className={styles.noTagsIcon} aria-hidden="true">!</div>
                  <div className={styles.noTagsContent}>
                    <div className={styles.noTagsTitle}>{_t("No forecast available")}</div>
                    <div className={styles.noTagsText}>
                      {!hasTags && !hasLanguage
                        ? _t("Please select at least one tag and one language to see the forecast preview.")
                        : !hasTags
                          ? _t("You haven't selected any tags yet. Choose at least one pill to see the forecast preview.")
                          : _t("Please select at least one language to see the forecast preview.")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className={styles.footer}>
        {isEn() ? (
          <div className={styles.helperText}>
            {_t("manage-subscriptions")}{" "}
            <a
              href="/research/japi/subscription_handshake"
              target="_blank"
              rel="noreferrer"
              className={styles.helperLink}
            >
              {_t("manage-subscriptions-link")}
            </a>
          </div>
        ) : (
          <div className={styles.helperText}>
            <a
              href="/research/japi/subscription_handshake"
              target="_blank"
              rel="noreferrer"
              className={styles.helperLink}
            >
              {_t("manage-subscriptions-link")}
            </a>
            {_t("manage-subscriptions")}
          </div>
        )}

        <div className={styles.actions}>
          
          <Button variant="outline-secondary" onClick={handleClose} className={styles.cancelBtn}>
            {_t("cancel-subscription")}
          </Button>
          {features.ShareSusbscriptionLink && (
            <button
              type="button"
              className={styles.shareBtn}
              onClick={handleShareLink}
              title={_t("share-subscription-link")}
              aria-label={_t("share-subscription-link")}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              {_t("share-subscription-link")}
            </button>
          )}
          <Button
            variant="primary"
            disabled={!hasLanguage || !hasTags || !isValidationComplete}
            onClick={() => onSubscribe(validatedTags)}
            className={styles.subscribeBtn}
          >
            {_t("subscribe-button-text")}
          </Button>
        </div>
      </Modal.Footer>
    </>
  );
}

function SubscriptionsModal({ size = "lg", centered = true, modalTitle }) {
  useSubscriptionDeepLink();

  const {
    modalShow,
    setModalShow,
    selectedSubscriptionTags,
    setSelectedSubscriptionTags,
    isSubscriptionFromPillsBar,
    selectedLanguages,
    setSelectedLanguages,
    selectedPub,
    defaultCategoriesAndTags,
  } = useSubscriptions();
  const { onSubscribe } = useOnSubscribe();
  const reportLang = useGetPreferences(PUB_LANG);
  const qs = useQueryParams();

  const handleClose = () => {
    setModalShow(false);
    setSelectedSubscriptionTags([]);
    setSelectedLanguages([]);
  };

  return (
    <Modal size={size} centered={centered} show={modalShow} onHide={handleClose} contentClassName={styles.modalContent}>
      <div className={styles.container}>
        <Modal.Header className={styles.header}>
          <Modal.Title as="div" className={styles.title}>
            <span className={`${styles.icon} ${styles.noCursor}`} aria-hidden="true">
              <SubscriptionsBellButton />
            </span>
            <p className={styles.title}>{modalTitle}</p>
          </Modal.Title>
          <button type="button" aria-label="Close" className={styles.closeBtn} onClick={handleClose}>
            <svg aria-hidden="true" className={styles.closeIcon} viewBox="0 0 24 24" focusable="false">
              <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </Modal.Header>

        {modalShow && (
          <SubscriptionsModalContent
            selectedSubscriptionTags={selectedSubscriptionTags}
            setSelectedSubscriptionTags={setSelectedSubscriptionTags}
            isSubscriptionFromPillsBar={isSubscriptionFromPillsBar}
            selectedLanguages={selectedLanguages}
            setSelectedLanguages={setSelectedLanguages}
            selectedPub={selectedPub}
            defaultCategoriesAndTags={defaultCategoriesAndTags}
            reportLang={reportLang}
            qs={qs}
            onSubscribe={onSubscribe}
            handleClose={handleClose}
          />
        )}
      </div>
    </Modal>
  );
}

export default SubscriptionsModal;